import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { svgIconClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Chip, { chipClasses } from '@mui/material/Chip';
import { t } from 'i18next';
import { pick } from 'lodash-es';
import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import useDispatchNumberFilterLabel from '~hooks/useDispatchNumberFilterLabel';
import { useStores } from '~store';
import { Filters as OrdersDispatchFilters } from '~store/OrdersDispatchStore';

interface OrdersViewAppliedFiltersRowProps {
  innerRef: MutableRefObject<HTMLDivElement | null>;
}

export default function OrdersViewAppliedFiltersRow({
  innerRef,
}: OrdersViewAppliedFiltersRowProps) {
  const { ordersDispatchStore } = useStores();
  const resolveFilterLabel = useDispatchNumberFilterLabel();
  const { appliedFilters } = useAppliedFilters();

  const shouldShowFilterChips = appliedFilters.length > 0;

  const handleClearFilterClick = useCallback(
    (filter: (typeof appliedFilters)[number]) => {
      const obj = filter.willClearOrdersDispatchFilters.reduce((obj, filterKey) => {
        if (filterKey === 'search') {
          obj.search = '';
        } else {
          obj[filterKey] = undefined;
        }

        return obj;
      }, {} as Partial<OrdersDispatchFilters>);

      ordersDispatchStore.setFilters(obj, true);
    },
    [ordersDispatchStore],
  );

  const handleClearAllFiltersClick = useCallback(() => {
    const obj = appliedFilters
      .flatMap((filter) => filter.willClearOrdersDispatchFilters)
      .reduce((obj, filterKey) => {
        if (filterKey === 'search') {
          obj.search = '';
        } else {
          obj[filterKey] = undefined;
        }

        return obj;
      }, {} as Partial<OrdersDispatchFilters>);

    ordersDispatchStore.setFilters(obj, true);
  }, [appliedFilters, ordersDispatchStore]);

  if (!shouldShowFilterChips) {
    return null;
  }

  return (
    <Box
      ref={(e: HTMLDivElement) => {
        if (e) {
          innerRef.current = e;
        }
      }}
      sx={(theme) => ({
        bgcolor: 'background.paper',
        borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        px: 2,
        py: 1,
      })}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {appliedFilters.map((appliedFilter) => (
          <FilterChip
            key={appliedFilter.key}
            label={resolveFilterLabel(
              appliedFilter.key === ApplicableFilterKey.DISPATCH_NUMBERS,
              appliedFilter.label,
            )}
            onClearFilterClick={() => handleClearFilterClick(appliedFilter)}
            values={appliedFilter.values}
          />
        ))}

        <ClearAllFiltersChip onClearAllFiltersClick={handleClearAllFiltersClick} />
      </Box>
    </Box>
  );
}

interface FilterChipProps {
  label: string;
  onClearFilterClick: () => void;
  values: string[];
}

const MAX_VISIBLE_ITEMS_PER_CHIP = 3;

function FilterChip({ label, onClearFilterClick, values }: FilterChipProps) {
  const content = values.slice(0, MAX_VISIBLE_ITEMS_PER_CHIP);
  const nonVisibleItemsCount = values.length - MAX_VISIBLE_ITEMS_PER_CHIP;

  const chipLabel = useMemo(() => {
    let chipLabel = `${label}: ${content.join(', ')}`;

    if (nonVisibleItemsCount > 0) {
      chipLabel = `${chipLabel}, +${nonVisibleItemsCount}`;
    }

    return chipLabel;
  }, [label, values]);

  return (
    <Chip
      deleteIcon={<CloseOutlined />}
      label={chipLabel}
      onDelete={onClearFilterClick}
      size="small"
      sx={(theme) => ({
        [`&.${chipClasses.root}`]: {
          bgcolor: theme.brandV2.colors.treadGray7,
          textTransform: 'none',
          width: 'fit-content',
        },
        [`& .${chipClasses.label}`]: {
          color: theme.brandV2.colors.treadBlack,
          fontSize: '12px',
          fontWeight: 500,
          pl: '10px',
        },
        [`'& .${svgIconClasses.root}'`]: { fontSize: '16px', mr: '6px' },
      })}
    />
  );
}

interface ClearAllFiltersChipProps {
  onClearAllFiltersClick: () => void;
}

function ClearAllFiltersChip({ onClearAllFiltersClick }: ClearAllFiltersChipProps) {
  return (
    <Chip
      color="default"
      icon={<CloseOutlined />}
      label={t('common.clear_all')}
      onClick={onClearAllFiltersClick}
      size="small"
      variant="outlined"
      sx={(theme) => ({
        [`&.${chipClasses.root}.MuiChip-colorDefault`]: {
          bgcolor: 'background.paper',
          border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          width: 'fit-content',
          '&:hover': { bgcolor: theme.brandV2.colors.treadGray8 },
        },
        [`& .${chipClasses.label}`]: {
          color: theme.brandV2.colors.treadBlack,
          fontSize: '12px',
          fontWeight: 500,
          pr: '10px',
        },
        [`& .${svgIconClasses.root}`]: { fontSize: '16px', ml: '6px' },
      })}
      data-ui="page-dispatch__orders-view-filters__clear-all-button"
    />
  );
}

const getStoredAvailableOptionsForFilter = (availableOptionsLocalStorageKey: string) => {
  const storedAvailableOptions = localStorage.getItem('ordersDispatch_availableOptions');
  const parsedStoredAvailableOptions = storedAvailableOptions
    ? JSON.parse(storedAvailableOptions)
    : {};

  return parsedStoredAvailableOptions[
    availableOptionsLocalStorageKey
  ] as FilterMenuOption[];
};

const collectHumanReadableValuesForFilter = (
  filterValuesInOrdersDispatchStore: unknown[] | undefined,
  availableOptions: FilterMenuOption[],
) => {
  const hasFiltersInStore = !!filterValuesInOrdersDispatchStore?.length;

  if (!hasFiltersInStore) {
    return [];
  }

  // Always show pills for active filters, even if labels can't be resolved
  if (!availableOptions?.length) {
    return [
      t('dispatch.dispatch_v2.filters.count_selected', {
        count: filterValuesInOrdersDispatchStore.length,
      }),
    ];
  }

  const resolvedLabels = filterValuesInOrdersDispatchStore.map((id) => {
    const storedValue = availableOptions.find((option) => {
      return option.value === id;
    });

    return storedValue?.label ?? '';
  });

  // If no labels could be resolved, show fallback count
  const hasAnyResolvedLabels = resolvedLabels.some((label) => label !== '');
  if (!hasAnyResolvedLabels) {
    return [
      t('dispatch.dispatch_v2.filters.count_selected', {
        count: filterValuesInOrdersDispatchStore.length,
      }),
    ];
  }

  // Filter out empty labels to avoid confusing display
  return resolvedLabels.filter((label) => label !== '');
};

enum ApplicableFilterKey {
  COLLABORATORS = 'collaborators',
  CUSTOMERS = 'customers',
  DEPARTMENTS = 'departments',
  DISPATCH_NUMBERS = 'dispatchNumbers',
  DISPATCH_STATUSES = 'dispatchStatuses',
  DRIVERS = 'drivers',
  DROP_OFF_SITES = 'dropOffSites',
  DUPLICATE_TICKETS = 'ticketPotentialDuplicate',
  EQUIPMENT = 'equipment',
  EQUIPMENT_OWNERS = 'equipmentOwners',
  EQUIPMENT_TYPES = 'equipmentTypes',
  FOREMEN = 'foremen',
  HAS_UNREAD_MESSAGES = 'hasUnreadMessages',
  JOB_STATES = 'jobStates',
  MATERIALS = 'materials',
  ORDER_STATES = 'orderStates',
  ORDER_STATES_REFINED = 'refinedOrderStates',
  PICK_UP_SITES = 'pickUpSites',
  PREVAILING_WAGE = 'prevailingWage',
  PROJECTS = 'projects',
  PROJECTS_EXTERNAL_IDS = 'projectsExternalIds',
  REQUESTERS = 'requesters',
  SALESPEOPLE = 'salespeople',
  SEARCH = 'search',
  SITE_TYPES = 'siteTypes',
  TICKET_TYPES = 'ticketTypes',
  VENDORS = 'vendors',
  AI_REVIEW_STATUSES = 'aiReviewStatuses',
}

type ApplicableFilter = {
  key: ApplicableFilterKey;
  label: string;
  willClearOrdersDispatchFilters: Array<keyof OrdersDispatchFilters>;
  getValues(ordersDispatchStoreFilters: OrdersDispatchFilters): string[];
};

type AppliedFilter = Pick<
  ApplicableFilter,
  'key' | 'label' | 'willClearOrdersDispatchFilters'
> & { values: string[] };

const applicableFilters: ApplicableFilter[] = [
  {
    key: ApplicableFilterKey.SEARCH,
    label: t('dispatch.dispatch_v2.filters.search'),
    willClearOrdersDispatchFilters: ['search'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const filtersInStore = ordersDispatchStoreFilters.search;
      const hasFiltersInStore = !!filtersInStore?.length;

      if (!hasFiltersInStore) {
        return [];
      }

      return [filtersInStore];
    },
  },
  {
    key: ApplicableFilterKey.CUSTOMERS,
    label: t('dispatch.dispatch_v2.filters.customer_account_ids'),
    willClearOrdersDispatchFilters: ['customerAccountIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('customers');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.customerAccountIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.COLLABORATORS,
    label: t('dispatch.dispatch_v2.filters.collaborators'),
    willClearOrdersDispatchFilters: ['collaboratorIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('collaborators');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.collaboratorIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.DEPARTMENTS,
    label: t('dispatch.dispatch_v2.filters.department_ids'),
    willClearOrdersDispatchFilters: ['departmentIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('departments');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.departmentIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.DISPATCH_NUMBERS,
    label: t('dispatch.dispatch_v2.filters.dispatch_numbers'),
    willClearOrdersDispatchFilters: ['dispatchNumberIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('dispatchNumbers');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.dispatchNumberIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.DRIVERS,
    label: t('dispatch.dispatch_v2.filters.driver_ids'),
    willClearOrdersDispatchFilters: ['driverIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('drivers');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.driverIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.DROP_OFF_SITES,
    label: t('dispatch.dispatch_v2.filters.drop_off_sites'),
    willClearOrdersDispatchFilters: ['dropoffSites'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('dropOffSites');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.dropoffSites,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.EQUIPMENT_TYPES,
    label: t('dispatch.dispatch_v2.filters.equipment_type_ids'),
    willClearOrdersDispatchFilters: ['equipmentTypes'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('equipmentTypes');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.equipmentTypes,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.EQUIPMENT,
    label: t('dispatch.dispatch_v2.filters.equipment'),
    willClearOrdersDispatchFilters: ['equipmentIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('equipment');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.equipmentIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.FOREMEN,
    label: t('dispatch.dispatch_v2.filters.foremen'),
    willClearOrdersDispatchFilters: ['foremen'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('foremen');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.foremen,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.DISPATCH_STATUSES,
    label: t('dispatch.dispatch_v2.filters.dispatch_statuses'),
    willClearOrdersDispatchFilters: ['dispatchStatuses'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('dispatchStatuses');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.dispatchStatuses,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.JOB_STATES,
    label: t('dispatch.dispatch_v2.filters.job_states'),
    willClearOrdersDispatchFilters: ['jobStates'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('jobStates');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.jobStates,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.MATERIALS,
    label: t('dispatch.dispatch_v2.filters.materials'),
    willClearOrdersDispatchFilters: ['materialIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('materials');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.materialIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.ORDER_STATES_REFINED,
    label: t('dispatch.dispatch_v2.filters.refined_order_states'),
    willClearOrdersDispatchFilters: ['refinedOrderStates'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('refinedOrderStates');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.refinedOrderStates,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.PICK_UP_SITES,
    label: t('dispatch.dispatch_v2.filters.pickup_site_ids'),
    willClearOrdersDispatchFilters: ['pickUpSites'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('pickUpSites');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.pickUpSites,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.PROJECTS,
    label: t('dispatch.dispatch_v2.filters.project_ids'),
    willClearOrdersDispatchFilters: ['projectIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('projects');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.projectIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.PROJECTS_EXTERNAL_IDS,
    label: t('dispatch.dispatch_v2.filters.projects_external_ids'),
    willClearOrdersDispatchFilters: ['projectsExternalIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('projectsExternalIds');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.projectsExternalIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.REQUESTERS,
    label: t('dispatch.dispatch_v2.filters.requester_ids'),
    willClearOrdersDispatchFilters: ['requesterIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('requesters');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.requesterIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.SALESPEOPLE,
    label: t('dispatch.dispatch_v2.filters.salespeople'),
    willClearOrdersDispatchFilters: ['salespersonIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('salespeople');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.salespersonIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.SITE_TYPES,
    label: t('dispatch.dispatch_v2.filters.site_types'),
    willClearOrdersDispatchFilters: ['siteTypes'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('siteTypes');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.siteTypes,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.EQUIPMENT_OWNERS,
    label: t('dispatch.dispatch_v2.filters.equipment_owners'),
    willClearOrdersDispatchFilters: ['equipmentOwnerCompanyIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('equipmentOwners');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.equipmentOwnerCompanyIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.VENDORS,
    label: t('dispatch.dispatch_v2.filters.vendor_account_ids'),
    willClearOrdersDispatchFilters: ['vendorAccountIds'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('vendors');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.vendorAccountIds,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.PREVAILING_WAGE,
    label: t('dispatch.dispatch_v2.filters.prevailing_wage'),
    willClearOrdersDispatchFilters: ['prevailingWage'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('prevailingWage');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.prevailingWage,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.DUPLICATE_TICKETS,
    label: t('dispatch.dispatch_v2.filters.duplicate_tickets'),
    willClearOrdersDispatchFilters: ['ticketPotentialDuplicate'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('duplicateTickets');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.ticketPotentialDuplicate,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.HAS_UNREAD_MESSAGES,
    label: t('dispatch.dispatch_v2.filters.unread_messages.applied_label'),
    willClearOrdersDispatchFilters: ['hasUnreadMessages'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      return ordersDispatchStoreFilters.hasUnreadMessages === true
        ? [t('dispatch.dispatch_v2.filters.unread_messages.applied_value')]
        : [];
    },
  },
  {
    key: ApplicableFilterKey.TICKET_TYPES,
    label: t('dispatch.dispatch_v2.filters.ticket_types'),
    willClearOrdersDispatchFilters: ['ticketTypes'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('ticketTypes');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.ticketTypes,
        storedValues,
      );
    },
  },
  {
    key: ApplicableFilterKey.AI_REVIEW_STATUSES,
    label: t('dispatch.dispatch_v2.filters.ai_review_statuses'),
    willClearOrdersDispatchFilters: ['aiReviewStatuses'],
    getValues(ordersDispatchStoreFilters: OrdersDispatchFilters) {
      const storedValues = getStoredAvailableOptionsForFilter('aiReviewStatuses');

      return collectHumanReadableValuesForFilter(
        ordersDispatchStoreFilters.aiReviewStatuses,
        storedValues,
      );
    },
  },
] as const;

function useAppliedFilters() {
  const { ordersDispatchStore } = useStores();

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);

  const getAppliedFilters = () => {
    return applicableFilters.reduce((acc, applicableFilter) => {
      const values = applicableFilter.getValues(ordersDispatchStore.filters);

      if (values.length) {
        acc.push({
          ...pick(applicableFilter, ['key', 'label', 'willClearOrdersDispatchFilters']),
          values,
        });
      }

      return acc;
    }, [] as AppliedFilter[]);
  };

  useEffect(() => {
    setAppliedFilters(getAppliedFilters());
  }, [JSON.stringify(ordersDispatchStore.filters)]);

  useEffect(() => {
    if (
      ordersDispatchStore.pendingFiltersUpdateAfterAssigneeUpdateHasBeenCommitedInLocalStorageAvailebleOptions
    ) {
      setAppliedFilters(getAppliedFilters());
    }
  }, [
    ordersDispatchStore.pendingFiltersUpdateAfterAssigneeUpdateHasBeenCommitedInLocalStorageAvailebleOptions,
  ]);

  return { appliedFilters };
}
